import { useEffect, useState } from 'react';
import { CampaignType_Enum as CampaignType, Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { FilterOptions } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import { useDeleteSegment, useListSegments } from '@frontend/api-bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { BulkEmailPrefixes, BulkTextPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, ModalControlModalProps, Tabs, Tray, useModalControl } from '@frontend/design-system';
import { MessageAllotmentsFiltersModal } from '../message-allotment';
import { SavedListModal } from '../saved-list/saved-list-modal';
import { UploadCSVModal } from '../saved-list/upload-csv-modal/upload-csv-modal';
import { ActiveTab, TrayMode } from '../types';
import { SegmentsModalContent } from './segments-modal-content';
import { TrayFooter } from './tray-footer';
import { TrayHeader } from './tray-header';
import { useContactLists } from './use-contact-lists';
import { useCountSegmentRecipients } from './use-count-segment-recipients';

type Props = {
  campaignId: string;
  campaignType: CampaignType;
  closeModal: () => void;
  duplicateCount?: number;
  handleCreateSegment: (filters?: FilterOptions, listId?: string) => void;
  handleDraftConversion: () => Promise<void>;
  isCreatingSegment: boolean;
  locationIds: string[];
  modalProps: ModalControlModalProps;
};

export const AudienceSelectionTray = ({
  campaignId,
  campaignType,
  closeModal,
  duplicateCount,
  handleCreateSegment,
  handleDraftConversion,
  isCreatingSegment,
  locationIds,
  modalProps,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  // TODO: Merge these two modes together and use just one.
  const [mode, setMode] = useState<TrayMode>('display');
  const [filters, setFilters] = useState<FilterOptions>({});
  const [activeTab, setActiveTab] = useState<ActiveTab>('segments');

  const { selectedOrgId: orgId } = useAppScopeStore();
  const { data: segments } = useListSegments({ campaignId, orgId });

  const { contactLists, setSelectedContactLists, selectedContactLists, ...savedListModalProps } = useContactLists(
    locationIds,
    campaignType
  );

  const trackingIdPrefix =
    campaignType === CampaignType.EMAIL ? BulkEmailPrefixes.Audience : BulkTextPrefixes.Recipients;

  // TODO: It is possible that a selected contact list has been deleted from the backend, but its contacts are still on the segment.
  // However, because the saved contact list no longer exists, it will not show up as selected here in this tray.
  // Figure out what to do in this situation.
  useEffect(() => {
    if (!segments) return;
    const selectedListIds = segments.filter((segment) => segment.listId).map((segment) => segment.listId as string);
    setSelectedContactLists(selectedListIds);
  }, [segments, setSelectedContactLists]);

  const { modalProps: messageAllotmentsModalProps, triggerProps: messageAllotmentsTriggerProps } = useModalControl();
  const recipientCount = useCountSegmentRecipients(campaignId, filters);
  const [recipientsToUpload, setRecipientsToUpload] = useState<Recipient[]>([]);

  const segmentRecipientsCount =
    segments?.filter((list) => !list.listId)?.reduce((acc, segment) => acc + (segment?.recipientCount || 0), 0) ?? 0;
  const contactListRecipientsCount =
    locationIds.length === 1
      ? selectedContactLists
          .map((listId) => contactLists.find((list) => list.listId === listId)?.recipientCount ?? 0)
          .reduce((acc, count) => acc + count, 0)
      : 0;
  const totalRecipientsCount = segmentRecipientsCount + contactListRecipientsCount;

  const getTrayHeaderTitle = () => {
    if (mode === 'form') {
      return t('Create a Segment');
    } else if (mode === 'csv') {
      return t('Upload CSV List');
    }
    return;
  };

  const getSourceCountText = () => {
    if (segmentRecipientsCount && contactListRecipientsCount) {
      return t('From Segments and Saved Lists');
    } else if (contactListRecipientsCount) {
      return t('From Saved Lists');
    } else if (segmentRecipientsCount) {
      return t('From Segments');
    }
    return activeTab === 'segments' ? t('From Segments') : t('From Saved Lists');
  };

  const { mutate: deleteSegment } = useDeleteSegment();

  return (
    <Tray
      {...modalProps}
      width='large'
      css={{
        padding: theme.spacing(4, 0),
        gap: 'unset',
        '.tray-header': {
          alignItems: mode === 'form' ? 'flex-start' : 'center',
          padding: theme.spacing(0, 4),
        },
      }}
    >
      <Tray.Header
        Buttons={
          <Button
            variant='tertiary'
            hoverLabel={t('close')}
            iconName='x'
            onClick={() => {
              closeModal();
              setMode('display');
            }}
            css={{ svg: { color: theme.colors.neutral90 } }}
          />
        }
      >
        <TrayHeader mode={mode} setMode={setMode} title={getTrayHeaderTitle()} trackingIdPrefix={trackingIdPrefix} />
      </Tray.Header>

      {/* show the tabs + saved lists only when the user has exactly one selected location because we don't know which location these contacts will be saved to or come from */}
      {locationIds.length === 1 ? (
        <Tabs controlledTab={activeTab} onChange={(value) => setActiveTab(value as ActiveTab)}>
          {/* only show the tabs in the display view */}
          {mode === 'display' ? (
            <Tabs.Bar fullWidth css={{ margin: theme.spacing(2, 4, 0) }}>
              <Tabs.Tab id='segments' trackingId={`${trackingIdPrefix}-segments-tab`}>
                {t('Segments')}
              </Tabs.Tab>
              <Tabs.Tab id='saved-lists' trackingId={`${trackingIdPrefix}-saved-lists-tab`}>
                {t('Saved Lists')}
              </Tabs.Tab>
            </Tabs.Bar>
          ) : null}
          <Tray.Body css={trayBodyStyles}>
            <Tabs.Panel controller='segments' css={tabPanelStyles}>
              <SegmentsModalContent
                campaignId={campaignId}
                filters={filters}
                handleDraftConversion={handleDraftConversion}
                isCreatingSegment={isCreatingSegment}
                locationIds={locationIds}
                mode={mode}
                segments={segments?.filter((segment) => !segment.listId)}
                setFilters={setFilters}
                setMode={setMode}
                trackingIdPrefix={trackingIdPrefix}
              />
            </Tabs.Panel>
            <Tabs.Panel controller='saved-lists' css={tabPanelStyles}>
              {mode === 'csv' ? (
                <UploadCSVModal
                  campaignId={campaignId}
                  campaignType={campaignType}
                  locationId={locationIds[0]}
                  setMode={setMode}
                  setRecipientsToUpload={setRecipientsToUpload}
                  trackingIdPrefix={trackingIdPrefix}
                />
              ) : (
                <SavedListModal
                  {...savedListModalProps}
                  campaignType={campaignType}
                  contactLists={contactLists}
                  recipientsToUpload={recipientsToUpload}
                  selectedContactLists={selectedContactLists}
                  setMode={setMode}
                  setRecipientsToUpload={setRecipientsToUpload}
                  trackingIdPrefix={trackingIdPrefix}
                />
              )}
            </Tabs.Panel>
          </Tray.Body>
        </Tabs>
      ) : (
        <Tray.Body css={trayBodyStyles}>
          <SegmentsModalContent
            campaignId={campaignId}
            filters={filters}
            handleDraftConversion={handleDraftConversion}
            isCreatingSegment={isCreatingSegment}
            locationIds={locationIds}
            mode={mode}
            segments={segments?.filter((segment) => !segment.listId)}
            setFilters={setFilters}
            setMode={setMode}
            trackingIdPrefix={trackingIdPrefix}
          />
        </Tray.Body>
      )}

      <TrayFooter
        // TODO: The closing/applying of the lists is kind of awkward. Rework it.
        // TODO: Invalidate the contact lists cache when closing/applying the modal
        recipientCount={mode === 'form' ? recipientCount : totalRecipientsCount}
        duplicateCount={duplicateCount}
        isValid={true}
        mode={mode}
        onCancel={() => {
          setMode('display');
          if (mode === 'display') closeModal();
        }}
        onConfirm={() => {
          const isSavedListsDisplay = mode === 'display' && activeTab === 'saved-lists';
          const isSegmentsDisplay = mode === 'display' && activeTab === 'segments';

          if (isSavedListsDisplay) {
            // delete the segments that are not selected but was previously selected (only the ones that have listId)
            const deletedListIds = segments?.filter(
              (segment) => segment.listId && !selectedContactLists.includes(segment.listId as string)
            );
            deletedListIds?.forEach((segment) => deleteSegment({ campaignId, segmentId: segment.segmentId, orgId }));

            // add the selected lists to the segments
            const newListIds = selectedContactLists.filter(
              (listId) => !segments?.find((segment) => segment.listId === listId)
            );
            newListIds.forEach((listId) => {
              handleCreateSegment(undefined, listId);
            });

            closeModal();
          } else if (isSegmentsDisplay) {
            closeModal();
          } else if (mode === 'form') {
            handleCreateSegment(filters);
            setMode('display');
            closeModal();
          } else if (mode === 'csv') {
            setMode('display');
          }
        }}
        sourceText={getSourceCountText()}
        tabMode={activeTab}
        trackingIdPrefix={trackingIdPrefix}
        triggerProps={messageAllotmentsTriggerProps}
      />
      {messageAllotmentsModalProps.show && (
        <MessageAllotmentsFiltersModal
          campaignId={campaignId}
          campaignType={campaignType}
          modalProps={messageAllotmentsModalProps}
          locationIds={filters.locationIds}
        />
      )}
    </Tray>
  );
};

const tabPanelStyles = { height: '100%' };
const trayBodyStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  padding: theme.spacing(0, 4, 2, 4),
};
